import React, { useEffect, useState } from 'react';
import { useCollectionStore } from '../../lib/collectionStore';
import { getAllSets } from '../../services/pokemonTcgApi';
import { Link } from 'react-router-dom';
import { slugify } from '../../utils/slugify';
import { ChevronDown, ChevronRight } from 'lucide-react';

export default function SeriesView() {
  const { collection } = useCollectionStore();
  const [allSets, setAllSets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSeries, setExpandedSeries] = useState<string[]>(['scarlet-violet']);
  const [sortBy, setSortBy] = useState<'name' | 'completion' | 'cards'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const fetchSets = async () => {
      try {
        const sets = await getAllSets();
        setAllSets(sets);
      } catch (error) {
        console.error('Error fetching sets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSets();
  }, []);

  // Group cards by series and set
  const seriesGroups = allSets.reduce((groups, set) => {
    const seriesName = set.series;
    const seriesId = slugify(seriesName);
    
    if (!groups[seriesId]) {
      groups[seriesId] = {
        id: seriesId,
        name: seriesName,
        sets: [],
        totalCards: 0,
        collectedCards: 0
      };
    }
    
    // Find cards in collection for this set
    const setCards = collection.filter(card => card.setId === set.id);
    const uniqueCardsInSet = setCards.length;
    
    groups[seriesId].sets.push({
      ...set,
      collectedCards: uniqueCardsInSet,
      completion: (uniqueCardsInSet / set.total) * 100
    });
    
    groups[seriesId].totalCards += set.total;
    groups[seriesId].collectedCards += uniqueCardsInSet;
    
    return groups;
  }, {} as Record<string, any>);

  // Sort series
  const sortedSeries = Object.values(seriesGroups).sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'completion':
        const completionA = (a.collectedCards / a.totalCards) * 100;
        const completionB = (b.collectedCards / b.totalCards) * 100;
        comparison = completionA - completionB;
        break;
      case 'cards':
        comparison = a.collectedCards - b.collectedCards;
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const toggleSeries = (seriesId: string) => {
    setExpandedSeries(prev => 
      prev.includes(seriesId) 
        ? prev.filter(s => s !== seriesId)
        : [...prev, seriesId]
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold">Series Overview</h2>
            <p className="text-white/60">
              View your collection organized by Pokémon TCG series.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'completion' | 'cards')}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white"
            >
              <option value="name">Sort by Name</option>
              <option value="completion">Sort by Completion</option>
              <option value="cards">Sort by Cards</option>
            </select>
            <button
              onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
              className="p-1.5 bg-white/5 border border-white/10 rounded-lg"
            >
              {sortDirection === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {sortedSeries.map(series => {
            const completion = (series.collectedCards / series.totalCards) * 100;
            const isExpanded = expandedSeries.includes(series.id);

            return (
              <div
                key={series.id}
                className="group relative bg-black/40 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden hover:bg-white/5 transition-colors isolate"
              >
                <button
                  onClick={() => toggleSeries(series.id)} 
                  className="relative w-full p-4 text-left"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-white/90">{series.name}</h3>
                        <div className="flex items-center gap-2">
                          <span className="bg-green-500/5 border border-green-500/20 rounded px-2 py-0.5 text-[10px] font-bold text-green-500">
                            {completion.toFixed(1)}%
                          </span>
                          {isExpanded ? (
                            <ChevronDown className="text-white/40" size={16} />
                          ) : (
                            <ChevronRight className="text-white/40" size={16} />
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-1.5 bg-white/10 rounded-full relative">
                          <div
                            className="absolute top-0 left-0 h-full bg-green-500 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(completion, 100)}%` }}
                          >
                            {completion > 0 && (
                              <div
                                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-black/80"
                                style={{ boxShadow: '0 0 8px 1px rgba(34, 197, 94, 0.3)' }}
                              />
                            )}
                          </div>
                          <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute h-full w-px bg-white/[0.07]" style={{ left: '25%' }} />
                            <div className="absolute h-full w-px bg-white/[0.07]" style={{ left: '50%' }} />
                            <div className="absolute h-full w-px bg-white/[0.07]" style={{ left: '75%' }} />
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-xs font-bold text-white">{series.collectedCards}</span>
                          <span className="text-[10px] text-white/40 font-bold">/ {series.totalCards}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="relative border-t border-white/10 p-4 bg-black/40 backdrop-blur-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {series.sets.map(set => (
                        <Link
                          key={set.id}
                          to={`/${slugify(series.name)}/${slugify(set.name)}`}
                          className="relative bg-white/5 border border-white/10 rounded-xl p-3 hover:bg-white/10 transition-colors isolate group"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            {set.images?.logo && (
                              <img
                                src={set.images.logo}
                                alt=""
                                className="h-5 w-auto object-contain opacity-60 group-hover:opacity-80 transition-opacity shrink-0"
                                loading="lazy"
                              />
                            )}
                            <h4 className="font-medium text-sm text-white/90 truncate">{set.name}</h4>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1 bg-white/10 rounded-full relative">
                              <div
                                className="absolute top-0 left-0 h-full bg-green-500 rounded-full transition-all duration-500"
                                style={{ width: `${Math.min(set.completion, 100)}%` }}
                              />
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <span className="text-[11px] font-bold text-white">{set.collectedCards}</span>
                              <span className="text-[9px] text-white/40 font-bold">/ {set.total}</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}